import { useState } from 'react';
import { Save } from 'lucide-react';
import type { CreateFirewallRulesetRequest, FirewallRulesetIssue } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import { isOneOf } from '../lib/isOneOf.ts';
import {
  appendRuleDraft,
  rulesFromRuleDrafts,
  validateRuleDrafts,
  type FirewallRuleDraft,
} from '../lib/firewallRuleDraft.ts';
import { FirewallIssueList } from './FirewallIssueList.tsx';
import { FirewallRuleListEditor } from './FirewallRuleListEditor.tsx';
import { Button, Dialog, FormField, Input, Select } from '../ui/index.ts';

/**
 * Create a ruleset with its first version (#1760).
 *
 * The created version is a DRAFT — `active_version` stays null until someone
 * activates it on the detail page — so this dialog changes nothing about live
 * traffic no matter what is typed into it. The copy says so, because a form
 * that looks like it arms a firewall and does not is its own kind of hazard.
 */

/**
 * The only doc version with a frozen evaluator
 * (`DEFAULT_FIREWALL_REGISTRY` in `packages/firewall/src/registry.ts`).
 * Anything else is refused server-side with `unknown-doc-version`, so it is
 * not offered as a choice rather than offered and rejected.
 */
const DEFAULT_FIREWALL_DOC_VERSION = 1;

const SCOPES = ['shared', 'binding'] as const;

export function FirewallRulesetCreateDialog({
  pending,
  serverIssues,
  onClose,
  onSubmit,
}: {
  readonly pending: boolean;
  readonly serverIssues: readonly FirewallRulesetIssue[];
  readonly onClose: () => void;
  readonly onSubmit: (body: CreateFirewallRulesetRequest) => void;
}): JSX.Element {
  const t = useT();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<(typeof SCOPES)[number]>('shared');
  const [keyBucketId, setKeyBucketId] = useState('');
  const [channelId, setChannelId] = useState('');
  const [extendsId, setExtendsId] = useState('');
  const [note, setNote] = useState('');
  const [drafts, setDrafts] = useState<readonly FirewallRuleDraft[]>(() => appendRuleDraft([]));

  const ownerComplete = scope === 'shared'
    || (keyBucketId.trim().length > 0 && channelId.trim().length > 0);
  const localIssues = validateRuleDrafts(drafts);
  const submittable = name.trim().length > 0
    && ownerComplete
    && drafts.length > 0
    && localIssues.length === 0;

  function submit(): void {
    const serialized = rulesFromRuleDrafts(drafts);
    if (!serialized.ok || !submittable) return;
    onSubmit({
      name: name.trim(),
      scope,
      ...(scope === 'binding'
        ? {
          owner_key_bucket_id: keyBucketId.trim(),
          owner_channel_id: channelId.trim(),
        }
        : {}),
      doc_version: DEFAULT_FIREWALL_DOC_VERSION,
      ...(extendsId.trim().length > 0 ? { extends_ruleset_id: extendsId.trim() } : {}),
      rules: serialized.rules,
      ...(note.trim().length > 0 ? { note: note.trim() } : {}),
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
      size="xl"
      title={t('firewall_rulesets.create.title')}
      description={t('firewall_rulesets.create.hint')}
      closeLabel={t('common.close')}
      testId="firewall-ruleset-create-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={pending}
            disabled={pending || !submittable}
            data-testid="firewall-ruleset-create-submit"
          >
            <Save aria-hidden="true" className="h-4 w-4" />
            <span>{t('firewall_rulesets.create.submit')}</span>
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            label={t('firewall_rulesets.field.name')}
            fieldId="firewall-ruleset-name"
            required
          >
            <Input
              id="firewall-ruleset-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={pending}
              data-testid="firewall-ruleset-name"
            />
          </FormField>
          <FormField
            label={t('firewall_rulesets.field.scope')}
            fieldId="firewall-ruleset-scope"
            hint={t(`firewall_rulesets.scope_hint.${scope}`)}
          >
            <Select
              id="firewall-ruleset-scope"
              value={scope}
              onValueChange={(next) => {
                if (isOneOf(next, SCOPES)) setScope(next);
              }}
              disabled={pending}
              testId="firewall-ruleset-scope"
              options={SCOPES.map((candidate) => ({
                value: candidate,
                label: t(`firewall_rulesets.scope.${candidate}`),
              }))}
            />
          </FormField>
        </div>

        {scope === 'binding' ? (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="firewall-ruleset-owner-fields">
            <FormField
              label={t('firewall_rulesets.field.owner_key_bucket')}
              fieldId="firewall-ruleset-owner-key-bucket"
              required
              hint={t('firewall_rulesets.field.owner_hint')}
            >
              <Input
                id="firewall-ruleset-owner-key-bucket"
                value={keyBucketId}
                mono
                onChange={(event) => setKeyBucketId(event.target.value)}
                disabled={pending}
                data-testid="firewall-ruleset-owner-key-bucket"
              />
            </FormField>
            <FormField
              label={t('firewall_rulesets.field.owner_channel')}
              fieldId="firewall-ruleset-owner-channel"
              required
              hint={t('firewall_rulesets.field.owner_hint')}
            >
              <Input
                id="firewall-ruleset-owner-channel"
                value={channelId}
                mono
                onChange={(event) => setChannelId(event.target.value)}
                disabled={pending}
                data-testid="firewall-ruleset-owner-channel"
              />
            </FormField>
          </div>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            label={t('firewall_rulesets.field.extends')}
            fieldId="firewall-ruleset-extends"
            hint={t('firewall_rulesets.field.extends_hint')}
          >
            <Input
              id="firewall-ruleset-extends"
              value={extendsId}
              mono
              onChange={(event) => setExtendsId(event.target.value)}
              disabled={pending}
              data-testid="firewall-ruleset-extends"
            />
          </FormField>
          <FormField
            label={t('firewall_rulesets.field.note')}
            fieldId="firewall-ruleset-note"
            hint={t('firewall_rulesets.field.note_hint')}
          >
            <Input
              id="firewall-ruleset-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={pending}
              data-testid="firewall-ruleset-note"
            />
          </FormField>
        </div>

        <FirewallRuleListEditor
          drafts={drafts}
          serverIssues={serverIssues}
          disabled={pending}
          idPrefix="firewall-create"
          onChange={setDrafts}
        />

        <FirewallIssueList issues={serverIssues} testId="firewall-ruleset-create-issues" />
      </div>
    </Dialog>
  );
}
